/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useContext } from "react";

// Context
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// types
import {  UserSuperStatus,  ComparisonNudgeItem  } from "@swiggy-private/cart-types";
import { CTATypes } from "@swiggy-private/super-ui";

// constants
import { PLAN_ADDITION_SOURCE } from "@cart-client/constants/index";
import { CLICK_ACTIONS, IMPRESSION_ACTIONS, SCREEN_NAME } from "@cart-client/analytics/constants";

interface useSubscriptionRenewalAnalyticsProps {
    header: string;
    variant: string;
    source: PLAN_ADDITION_SOURCE;
    userSuperStatus: UserSuperStatus;
}

export const useSubscriptionRenewalAnalytics = ({
    header,
    variant,
    userSuperStatus,
    source,
}: useSubscriptionRenewalAnalyticsProps) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    // Helper function to compute context value for analytics events
    const getContextValue = useCallback(
        (subscriptionRenewalNudgeItemComms: ComparisonNudgeItem | null) => {
            const planDetails = subscriptionRenewalNudgeItemComms?.plan_details;

            return JSON.stringify({
                outerHeader: header,
                variant: variant,
                planid: planDetails?.plan_id,
                source,
                userSuperStatus,
                plan_type: subscriptionRenewalNudgeItemComms?.plan_details?.type,
                subtext: subscriptionRenewalNudgeItemComms?.sub_titles?.[0],
                planPrice: subscriptionRenewalNudgeItemComms?.plan_details?.final_price,
                ctaText: subscriptionRenewalNudgeItemComms?.ctas?.[1]?.text,
                planAdded: subscriptionRenewalNudgeItemComms?.ctas?.[1]?.type === CTATypes.ONE_TAP_REMOVE,
            });
        },
        [header, variant, source, userSuperStatus],
    );

    const onImpressionSubscriptionRenewalPlanNudge = useCallback(
        (subscriptionRenewalNudgeItemComms: ComparisonNudgeItem | null) => {
            const isBlackPlan = subscriptionRenewalNudgeItemComms?.plan_details?.type === "PLAN_TYPE_BLACK";

            impressionEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: isBlackPlan
                    ? IMPRESSION_ACTIONS.IMPRESSION_SUBSCRIPTION_RENEWAL_NUDGE_BLACK_PLAN
                    : IMPRESSION_ACTIONS.IMPRESSION_SUBSCRIPTION_RENEWAL_NUDGE_ONE_PLAN,
                context: getContextValue(subscriptionRenewalNudgeItemComms),
            });
        },
        [getContextValue, impressionEvent],
    );

    const onImpressionSubscriptionRenewalRemovalCta = useCallback(
        (subscriptionRenewalNudgeItemComms: ComparisonNudgeItem | null) => {
            const isBlackPlan = subscriptionRenewalNudgeItemComms?.plan_details?.type === "PLAN_TYPE_BLACK";

            impressionEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: isBlackPlan
                    ? IMPRESSION_ACTIONS.IMPRESSION_SUBSCRIPTION_RENEWAL_REMOVAL_CTA_BLACK
                    : IMPRESSION_ACTIONS.IMPRESSION_SUBSCRIPTION_RENEWAL_REMOVAL_CTA_ONE,
                context: getContextValue(subscriptionRenewalNudgeItemComms),
            });
        },
        [getContextValue, impressionEvent],
    );

    const onClickAddPlanCta = useCallback(
        (subscriptionRenewalNudgeItemComms: ComparisonNudgeItem | null) => {
            const isBlackPlan = subscriptionRenewalNudgeItemComms?.plan_details?.type === "PLAN_TYPE_BLACK";

            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: isBlackPlan
                    ? CLICK_ACTIONS.CLICK_BLACK_PLAN_ADD_BUTTON
                    : CLICK_ACTIONS.CLICK_ONE_PLAN_ADD_BUTTON,
                context: getContextValue(subscriptionRenewalNudgeItemComms),
            });
        },
        [getContextValue, clickEvent],
    );
    const onClickRemovePlanCta = useCallback(
        (subscriptionRenewalNudgeItemComms: ComparisonNudgeItem | null) => {
            const isBlackPlan = subscriptionRenewalNudgeItemComms?.plan_details?.type === "PLAN_TYPE_BLACK";

            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: isBlackPlan
                    ? CLICK_ACTIONS.CLICK_BLACK_PLAN_REMOVE_BUTTON
                    : CLICK_ACTIONS.CLICK_ONE_PLAN_REMOVE_BUTTON,
                context: getContextValue(subscriptionRenewalNudgeItemComms),
            });
        },
        [getContextValue, clickEvent],
    );

    const onClickComparePlanCta = useCallback(
        (subscriptionRenewalNudgeItemComms: ComparisonNudgeItem | null) => {
            const isBlackPlan = subscriptionRenewalNudgeItemComms?.plan_details?.type === "PLAN_TYPE_BLACK";

            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: isBlackPlan
                    ? CLICK_ACTIONS.CLICK_COMPARE_PLAN_ON_SUBSCRIPTION_RENEWAL_BLACK_PLAN
                    : CLICK_ACTIONS.CLICK_COMPARE_PLAN_ON_SUBSCRIPTION_RENEWAL_ONE_PLAN,
                context: getContextValue(subscriptionRenewalNudgeItemComms),
            });
        },
        [getContextValue, clickEvent],
    );

    const onImpressionPlanRemovalHalfcard = useCallback(
        (context: string) => {
            impressionEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: IMPRESSION_ACTIONS.IMPRESSION_SUBSCRIPTION_RENEWAL_PLAN_REMOVAL_HALFCARD,
                context,
            });
        },
        [impressionEvent],
    );

    const onClickRemovePlanRemovalHalfcard = useCallback(
        (context: string) => {
            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: CLICK_ACTIONS.CLICK_SUBSCRIPTION_RENEWAL_PLAN_REMOVAL_HALFCARD,
                context,
            });
        },
        [clickEvent],
    );

    return {
        onImpressionSubscriptionRenewalRemovalCta,
        onImpressionSubscriptionRenewalPlanNudge,
        onClickAddPlanCta,
        onClickRemovePlanCta,
        onClickComparePlanCta,
        onImpressionPlanRemovalHalfcard,
        onClickRemovePlanRemovalHalfcard,
    };
};
